import {
  type MutableSelectOption,
  type MutableSelectOptions,
  type SelectOption,
  type SelectOptionColor,
  type SelectOptionJSON,
  type SelectOptions
} from '@databases/model/field';
import { SelectOptionColors } from '@databases/model/field';
import { type Op } from 'quill-delta';
import * as Y from 'yjs';

import { type LazyValue } from './helpers/lazy-value.ts';
import { SynchronizedArray } from './helpers/synchronized-array.ts';
import { SynchronizedRecord } from './helpers/synchronized-record.ts';
import { type DisposableElement, type WrappedYType } from './helpers/types.ts';
import { moveArrayItem } from './helpers/utils.ts';

const SelectOptionProperties = {
  id: 'i',
  label: 'l',
  color: 'c'
};

export class YSelectOption
  extends SynchronizedRecord
  implements SelectOption, MutableSelectOption
{
  static mapper(value: unknown): YSelectOption | undefined {
    if (value instanceof Y.Map) {
      return new YSelectOption(value);
    }
  }

  get id(): string {
    return this.getString(SelectOptionProperties.id, '');
  }

  get label(): string {
    return this.getText(SelectOptionProperties.label)?.toJSON() ?? '';
  }

  applyLabelDelta(delta: Op[]): void {
    if (!this.getText(SelectOptionProperties.label)) {
      this.set(SelectOptionProperties.label, new Y.Text());
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.getText(SelectOptionProperties.label)!.applyDelta(delta);
  }

  get color(): SelectOptionColor {
    return this.getStringEnum(
      SelectOptionProperties.color,
      SelectOptionColors,
      SelectOptionColors[0]
    );
  }

  set color(color: SelectOptionColor) {
    this.set(SelectOptionProperties.color, color);
  }

  toJSON(): SelectOptionJSON {
    return {
      id: this.id,
      label: this.label,
      color: this.color
    };
  }

  static fromJSON(json: SelectOptionJSON) {
    return new YSelectOption(
      new Y.Map([
        [SelectOptionProperties.id, json.id],
        [SelectOptionProperties.label, new Y.Text(json.label)],
        [SelectOptionProperties.color, json.color]
      ])
    );
  }
}

export class YSelectOptions
  implements SelectOptions, MutableSelectOptions, DisposableElement, WrappedYType
{
  private array: SynchronizedArray<YSelectOption>;

  constructor(input: Y.Array<unknown> | LazyValue<Y.Array<unknown>>) {
    this.array = new SynchronizedArray(input, YSelectOption.mapper);
  }

  get options(): YSelectOption[] {
    return this.array.toArray();
  }

  add(option: SelectOptionJSON): void {
    this.array.push([YSelectOption.fromJSON(option)]);
  }

  reorder(index: number, newIndex: number): void {
    moveArrayItem<YSelectOption>(this.array, index, newIndex, (val) =>
      YSelectOption.fromJSON(val.toJSON())
    );
  }

  delete(index: number): void {
    this.array.delete(index);
  }

  dispose(): void {
    this.array.dispose();
  }

  toJSON(): SelectOptionJSON[] {
    return this.array.toArray().map((option) => option.toJSON());
  }

  static fromJSON(json: SelectOptionJSON[]): YSelectOptions {
    return new YSelectOptions(
      Y.Array.from<any>(json.map((option) => YSelectOption.fromJSON(option).unwrap()))
    );
  }

  unwrap(): Y.AbstractType<any> | undefined {
    return this.array.unwrap();
  }
}
